import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import { logger } from '@/composables/useLogger'
import { captureError, isCriticalError } from '@/utils/sentry'

export const useOnboardingStore = defineStore('onboarding', () => {
  const isOpen = ref(false)
  const persistent = ref(false)
  const hasAutoTriggered = ref(false)

  // Auto-open the non-dismissible dialog on the user's first login. Guarded so
  // it fires at most once per app session.
  function autoTrigger(): void {
    if (hasAutoTriggered.value) return
    hasAutoTriggered.value = true
    persistent.value = true
    isOpen.value = true
  }

  // Reopen on demand from the Settings Help card — dismissible this time.
  function openManual(): void {
    persistent.value = false
    isOpen.value = true
  }

  // Called when the stepper is completed. Persists "seen" only for the
  // first-time (persistent) showing.
  async function finish(): Promise<void> {
    isOpen.value = false
    if (persistent.value) {
      try {
        await useAuthStore().markOnboardingSeen()
      } catch (e) {
        logger.error('Failed to persist onboarding-seen', {}, e instanceof Error ? e : undefined)
        // Critical (5xx/network) failures are already captured by the apiClient
        // response interceptor; only report non-critical failures here to avoid
        // double-reporting the same error to Sentry.
        if (!isCriticalError(e)) {
          captureError(e instanceof Error ? e : new Error('Failed to persist onboarding-seen'), {
            store: 'onboarding',
            action: 'finish'
          })
        }
      }
    }
    persistent.value = false
  }

  // Manual (non-persistent) close via the X button.
  function close(): void {
    isOpen.value = false
  }

  return { isOpen, persistent, hasAutoTriggered, autoTrigger, openManual, finish, close }
})
